import { useEffect, useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import api, { formatErr } from "../lib/apiClient";
import { fmtDate, todayIso } from "../lib/format";

export default function Mileage() {
  const [items, setItems] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [err, setErr] = useState("");

  const blank = { date: todayIso(), start_location: "", end_location: "", distance_km: 0, purpose: "", job_id: null };

  const load = async () => {
    const [{ data }, { data: jj }] = await Promise.all([api.get("/mileage"), api.get("/jobs?include_closed=true")]);
    setItems(data); setJobs(jj);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault(); setErr("");
    try {
      const payload = { ...editing, distance_km: Number(editing.distance_km), job_id: editing.job_id || null };
      if (editing.id) await api.put(`/mileage/${editing.id}`, payload);
      else await api.post("/mileage", payload);
      setShowForm(false); setEditing(null); await load();
    } catch (e) { setErr(formatErr(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/mileage/${id}`); await load();
  };

  const totalKm = items.reduce((s, x) => s + Number(x.distance_km || 0), 0);

  return (
    <div data-testid="mileage-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div><h1 className="font-display text-3xl font-bold tracking-tight">Mileage</h1><p className="text-sm text-slate-500 mt-1">{items.length} trip{items.length !== 1 ? "s" : ""} · {totalKm.toFixed(1)} km total</p></div>
        <button onClick={() => { setEditing(blank); setShowForm(true); }} data-testid="add-mileage-btn" className="self-start px-4 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium flex items-center gap-2"><Plus size={16} /> Add Trip</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Date</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">From</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">To</th>
                <th className="px-4 py-3 text-right text-[11px] uppercase tracking-wider text-slate-500 font-medium">Distance</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Purpose / Job</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">No trips yet.</td></tr>}
              {items.map((x) => (
                <tr key={x.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-600">{fmtDate(x.date)}</td>
                  <td className="px-4 py-3">{x.start_location}</td>
                  <td className="px-4 py-3">{x.end_location}</td>
                  <td className="px-4 py-3 text-right tabular font-medium">{Number(x.distance_km).toFixed(1)} km</td>
                  <td className="px-4 py-3"><div>{x.purpose || "—"}</div>{x.job_id && <div className="text-xs text-slate-500">{jobs.find((j) => j.id === x.job_id)?.number}</div>}</td>
                  <td className="px-4 py-3 text-right"><button onClick={() => { setEditing(x); setShowForm(true); }} className="text-xs text-[#0F4C81] hover:underline mr-2">Edit</button><button onClick={() => del(x.id)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white rounded-md max-w-lg w-full p-6 space-y-3">
            <div className="flex justify-between items-center"><h3 className="font-display text-xl font-bold">{editing.id ? "Edit" : "New"} Trip</h3><button type="button" onClick={() => setShowForm(false)} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button></div>
            {err && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{err}</div>}
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs uppercase text-slate-700 tracking-wider">Date *</label><input type="date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} required className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md" /></div>
              <div><label className="text-xs uppercase text-slate-700 tracking-wider">Distance (km) *</label><input type="number" step="0.1" value={editing.distance_km} onChange={(e) => setEditing({ ...editing, distance_km: e.target.value })} required className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md tabular" /></div>
              <div className="col-span-2"><label className="text-xs uppercase text-slate-700 tracking-wider">Start Location *</label><input value={editing.start_location} onChange={(e) => setEditing({ ...editing, start_location: e.target.value })} required className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md" /></div>
              <div className="col-span-2"><label className="text-xs uppercase text-slate-700 tracking-wider">End Location *</label><input value={editing.end_location} onChange={(e) => setEditing({ ...editing, end_location: e.target.value })} required className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md" /></div>
              <div className="col-span-2"><label className="text-xs uppercase text-slate-700 tracking-wider">Purpose</label><input value={editing.purpose} onChange={(e) => setEditing({ ...editing, purpose: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md" /></div>
              <div className="col-span-2"><label className="text-xs uppercase text-slate-700 tracking-wider">Link to Job</label>
                <select value={editing.job_id || ""} onChange={(e) => setEditing({ ...editing, job_id: e.target.value || null })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md">
                  <option value="">— None —</option>
                  {jobs.map((j) => <option key={j.id} value={j.id}>{j.number} {j.job_type ? "· " + j.job_type : ""}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button type="submit" className="px-4 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium">Save</button>
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
